import { closeIcon, triangleAlert } from "@/assets/icons";
import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

@customElement("sb-members-action-modal")
export class SbActionModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: String }) title = "";
  @property({ type: String }) description = "";
  @property({ type: String }) infoText = "";
  @property({ type: String }) confirmLabel = "Confirm";
  @property({ type: String }) cancelLabel = "Cancel";
  @property({ type: String }) variant: "default" | "danger" = "default";

  @state() private isLoading = false;

  // Callbacks - now support async functions
  @property({ attribute: false }) onConfirm?: () => void | Promise<void>;
  @property({ attribute: false }) onCancel?: () => void | Promise<void>;

  protected createRenderRoot() {
    return this;
  }

  private handleClose() {
    if (this.isLoading) return; // Prevent closing while loading

    this.open = false;
    this.onCancel?.();
    this.dispatchEvent(new CustomEvent("modal-closed", { bubbles: true }));
  }

  private handleCancel() {
    if (this.isLoading) return; // Prevent closing while loading

    this.open = false;
    this.onCancel?.();
    this.dispatchEvent(new CustomEvent("modal-cancelled", { bubbles: true }));
  }

  private async handleConfirm() {
    if (this.isLoading) return; // Prevent multiple clicks

    try {
      this.isLoading = true;

      // Call the confirm handler (could be sync or async)
      const result = this.onConfirm?.();

      // If it returns a promise, await it
      if (result instanceof Promise) {
        await result;
      }

      // Only close if the action was successful
      this.open = false;
      this.dispatchEvent(new CustomEvent("modal-confirmed", { bubbles: true }));
    } catch (error) {
      // Handle error - the parent component can throw to prevent closing
      console.error("Action failed:", error);
      // Optionally dispatch an error event
      this.dispatchEvent(
        new CustomEvent("modal-error", {
          bubbles: true,
          detail: { error },
        }),
      );
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return html`
      <sb-modal
        part="no_min_width"
        .isOpen=${this.open}
        @close=${this.handleClose}
        .showClose=${false}
      >
        <div part="container" class="sb-flex sb-flex-col sb-gap-4">
          <div class="sb-pt-6 sb-px-4 sb-flex sb-justify-between">
            <div>
              <rtg-typography-h level="h4">${this.title}</rtg-typography-h>
              <rtg-typography-muted>${this.description}</rtg-typography-muted>
            </div>

            <rtg-button
              variant="ghost"
              size="icon-sm"
              part="modal-close-button"
              @click=${this.handleClose}
            >
              ${closeIcon()}
            </rtg-button>
          </div>
          <!-- Custom content via slot -->
          ${this.infoText
            ? html` <div part="info-container">
                <span>${triangleAlert()}</span>
                <span>${this.infoText}</span>
              </div>`
            : nothing}
          <div class="sb-flex sb-justify-end sb-gap-2 sb-mt-2 sb-pb-6 sb-px-4">
            <rtg-button
              variant="secondary"
              @click=${this.handleCancel}
              ?disabled=${this.isLoading}
            >
              ${this.cancelLabel}
            </rtg-button>
            <rtg-button
              variant=${this.variant === "danger" ? "destructive" : "default"}
              @click=${this.handleConfirm}
              ?disabled=${this.isLoading}
            >
              ${this.isLoading
                ? html`<rtg-spinner size="sm"></rtg-spinner> ${this
                      .confirmLabel}`
                : this.confirmLabel}
            </rtg-button>
          </div>
        </div>
      </sb-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-members-action-modal": SbActionModal;
  }
}
